/**
 * The TanStack Query client — story 0.6 AC-11.
 *
 * 🔒 **Creating it registers `clear()` as logout teardown.** That closes the half
 * of story 0.3's AC-10 that has been open since 13/08: logout wipes the session,
 * but there was no cache to wipe. Nothing fails today if this is forgotten — the
 * symptom is user B logging in on user A's machine and seeing A's tenant rows for
 * the frame before the refetch lands. BR-19 makes that a data-segregation bug, not
 * a cosmetic one.
 *
 * The registration goes through `registrarLimpezaDeSessao()` rather than `auth/`
 * importing this file, because the bridge must keep pointing one way: `api/`
 * imports `auth/`, never the reverse (§5.5).
 */
import { QueryClient } from '@tanstack/react-query'

import { registrarLimpezaDeSessao } from '@/auth/sessionBridge'
import { aoTrocarDeCliente } from '@/tenant/trocaDeCliente'

import { ErroDeApi } from './clients/criarCliente'

/** Statuses where the server has already given its answer, and it will not change. */
const NAO_ADIANTA_REPETIR = new Set([400, 401, 403, 404, 409, 422])

function deveRepetir(tentativa: number, erro: unknown): boolean {
  const status = erro instanceof ErroDeApi ? erro.status : (erro as { status?: number })?.status
  // A 401 has already sent the user to /login via the bridge; retrying it just
  // delays the redirect behind two more failed round-trips.
  if (typeof status === 'number' && NAO_ADIANTA_REPETIR.has(status)) return false
  // A transport failure is worth exactly one more try. Beyond that the user is
  // offline and deserves an error, not a spinner.
  return tentativa < 1
}

export function criarQueryClient(): QueryClient {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        retry: deveRepetir,
        // The platform is explicitly NOT mission-critical (§8) and the data is
        // reported monthly, so refetching on every window focus buys noise and
        // Lambda invocations rather than freshness.
        refetchOnWindowFocus: false,
        staleTime: 30_000,
      },
      mutations: { retry: false },
    },
  })

  registrarLimpezaDeSessao(() => queryClient.clear())

  /**
   * 🔒 Story 0.5 AC-5 — **switching client invalidates; it never merges.**
   *
   * Every tenant-scoped key starts `['cliente', idCliente, ...]` (see
   * `queries/chaveDeQuery.ts`), so TanStack Query already treats two tenants as two
   * distinct queries and cannot mix them in one entry. What that alone does NOT do
   * is forget the client the user just left — its data stays cached and would be
   * served instantly if they switched back, possibly hours stale.
   *
   * `removeQueries` on the outgoing tenant is the difference between "the cache is
   * correct" and "the cache is correct AND does not hold a second client's figures
   * in memory". On a multi-tenant platform that is a data-handling property, not a
   * performance one.
   */
  aoTrocarDeCliente(() => {
    queryClient.removeQueries({ queryKey: ['cliente'] })
  })

  return queryClient
}
